import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Sparkles, ShieldAlert } from "lucide-react";
import { formatError } from "@/lib/api";

const LOCKOUT_MESSAGE = "Too many sign-in attempts. Please try again in a few minutes.";

function formatCountdown(secs) {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  if (m > 0) return `${m}m ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}

// OP-023: product rules only — no static usage figures. 2.5x mirrors the backend outlier threshold.
const PRODUCT_RULES = [["3", "Plateformes prises en charge"], ["≥ 2,5x", "Seuil outlier (médiane du profil)"], ["Texte", "Interprétation IA, si activée"]];

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [lockRemaining, setLockRemaining] = useState(0);
  const { login, sessionExpired } = useAuth();
  const nav = useNavigate();

  // Server-driven lockout countdown; auto-clears without a page refresh.
  useEffect(() => {
    if (lockRemaining <= 0) return;
    const id = setInterval(() => {
      setLockRemaining((r) => (r <= 1 ? 0 : r - 1));
    }, 1000);
    return () => clearInterval(id);
  }, [lockRemaining]);

  const locked = lockRemaining > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (locked) return;
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      if (err?.code === "SESSION_REJECTED") {
        toast.error("Identifiants acceptés, mais le serveur a refusé la session ouverte. Aucune session n'est active : reconnectez-vous.");
      } else if (err?.response?.status === 429) {
        const header = err.response.headers?.["retry-after"];
        const retry = parseInt(header, 10);
        setLockRemaining(Number.isFinite(retry) && retry > 0 ? retry : 300);
      } else {
        toast.error(formatError(err));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-background">
      <div className="hidden md:flex md:w-1/2 relative border-r border-border bg-[hsl(var(--sidebar))] items-center justify-center p-12 bg-grid">
        <div className="relative max-w-md">
          <div className="flex items-center gap-2 mb-8">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground">
              <Sparkles className="h-5 w-5" />
            </div>
            <span className="font-display text-xl font-extrabold">OutlierPulse</span>
          </div>
          <h1 className="font-display text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
            Repérez les vidéos courtes <span className="text-primary">qui surperforment leur créateur.</span>
          </h1>
          <p className="mt-4 text-muted-foreground text-base leading-relaxed" data-testid="login-product-summary">
            Suivez des créateurs TikTok, Instagram Reels et YouTube Shorts. Une vidéo est signalée comme outlier lorsque ses vues atteignent au moins 2,5 fois la médiane du profil suivi. Lorsque l’IA est activée, elle interprète seulement les titres, légendes et textes disponibles : ni l’image, ni l’audio, ni le format de la vidéo.
          </p>
          <div className="mt-8 grid grid-cols-3 gap-4" data-testid="login-product-rules">
            {PRODUCT_RULES.map(([v, l]) => (
              <div key={l} className="rounded-lg border border-border bg-card/60 p-3">
                <div className="font-mono text-lg font-bold">{v}</div>
                <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{l}</div>
              </div>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground" data-testid="login-product-rules-note">Règles de fonctionnement, pas des statistiques de votre espace.</p>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <Card className="w-full max-w-sm p-6">
          <h2 className="font-display text-2xl font-bold">Welcome back</h2>
          <p className="text-sm text-muted-foreground mt-1">Sign in to your OutlierPulse workspace.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            {sessionExpired && (
              <p role="status" className="rounded-md border border-border bg-muted px-3 py-2 text-sm" data-testid="login-session-expired">
                Votre session a expiré ou a été révoquée. Reconnectez-vous pour continuer.
              </p>
            )}
            {locked && (
              <div
                className="flex items-start gap-2.5 rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm text-amber-700 dark:text-amber-400"
                role="alert"
                data-testid="login-lockout-banner"
              >
                <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">{LOCKOUT_MESSAGE}</p>
                  <p className="mt-0.5 text-xs opacity-80" data-testid="login-lockout-countdown">
                    You can try again in {formatCountdown(lockRemaining)}.
                  </p>
                </div>
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" className="w-full" disabled={loading || locked} data-testid="login-submit-button">
              {locked ? `Try again in ${formatCountdown(lockRemaining)}` : loading ? "Signing in..." : "Sign in"}
            </Button>
          </form>

          <div className="mt-3 text-center">
            <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-primary hover:underline" data-testid="forgot-password-link">
              Forgot your password?
            </Link>
          </div>

          <div className="mt-4 text-sm text-muted-foreground text-center">
            No account?{" "}
            <Link to="/register" className="text-primary hover:underline font-medium" data-testid="go-to-register-link">
              Create one
            </Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
